import * as Sentry from "@sentry/vue";
import { reactive, computed, ref, isRef } from "vue";
import { useToastsStore } from "./toastsStore";
import { useLegacyLastErrorStore } from "./legacyLastErrorStore";
import { IgnoredErrorMessages } from "@/utils/constants/ErrorConstants";

const updateValue = (target, value, key) => {
  if (target[key] === undefined || target[key] === value) return;
  if (isRef(target[key])) target[key].value = value;
  else target[key] = value;
};

export const exceptionWrapper = async (
  f = () => null,
  options = { loading: null, error: null, toast: null, throw: null },
) => {
  try {
    updateValue(options, true, "loading");
    updateValue(options, false, "error");
    const result = await f();
    updateValue(options, false, "loading");
    return result;
  } catch (error) {
    // Abort path: a newer call (typically via useAbortable) is already in
    // flight and owns `loading`/`error` now — don't touch them, or we'd
    // flash the table out of skeleton state while the new request is still
    // loading. Optional chaining guards against `throw null/undefined`.
    if (error?.name === "AbortError") return null;

    updateValue(options, false, "loading");
    if (error?.message && !IgnoredErrorMessages.includes(error.message))
      Sentry.captureException(error);
    options.toast && useToastsStore().create({ type: "error", body: error?.message });
    updateValue(options, true, "error");
    if (options.throw) throw error;
    return null;
  }
};

export const catchable = async ({
  t = () => null,
  c = () => null,
  f = () => null,
  loadable = false,
  throwable = false,
}) => {
  const legacyLastErrorStore = useLegacyLastErrorStore();
  try {
    loadable && legacyLastErrorStore.setLoading(true);
    return await t();
  } catch (error) {
    legacyLastErrorStore.sendError(error);
    if (error.message && !IgnoredErrorMessages.includes(error.message))
      Sentry.captureException(error);
    await c(error);
    if (throwable) throw error;
    return null;
  } finally {
    loadable && legacyLastErrorStore.setLoading(false);
    await f();
  }
};

export const useTableData = (
  { perPageInit = 10, totalItems = 0 } = { perPageInit: 10, totalItems: 0 },
) => {
  const totalPagesInit = totalItems && Math.max(Math.ceil(totalItems / perPageInit), 1);
  const page = ref(totalItems ? 1 : 0);
  const perPage = ref(perPageInit);
  const totalCount = ref(totalItems || perPageInit);
  const totalPages = ref(totalPagesInit || 1);
  const loadingMore = ref(false);
  const table = reactive({
    page,
    perPage,
    totalCount,
    totalPages,
    showing: computed(() => Math.min(page.value * perPage.value, totalCount.value)),
    loadMore: computed(() =>
      Math.max(Math.min(totalCount.value - page.value * perPage.value, perPage.value), 0),
    ),
  });

  const resetPage = () => {
    page.value = 0;
    perPage.value = perPageInit;
    totalCount.value = perPageInit;
    totalPages.value = 1;
  };

  const setPagination = pagination => {
    page.value = pagination.page;
    totalCount.value = pagination.total_count;
    totalPages.value = pagination.total_pages;
  };

  const reloadAllLoaded = async loader => {
    const oldPage = page.value;
    const oldPerPage = perPage.value;
    page.value = 0;
    perPage.value = Math.max(oldPage * oldPerPage, oldPerPage);
    try {
      await loader();
    } finally {
      page.value = oldPage;
      perPage.value = oldPerPage;
    }
  };

  return { table, resetPage, setPagination, reloadAllLoaded, loadingMore };
};
